"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  CheckboxRow,
  ResultBanner,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
  type Verdict,
} from "@/components/tools/ui";

/* Logic follows the Confined Spaces Regulations 1997 (SI 1997/1713) and HSE
   ACOP L101. A confined space is a SUBSTANTIALLY ENCLOSED place where, by
   virtue of that enclosure, a "specified risk" is reasonably foreseeable.
   The five specified risks are quoted verbatim from reg 2(1):
     (a) serious injury from fire or explosion;
     (b) loss of consciousness from increase in body temperature (heat);
     (c) loss of consciousness OR asphyxiation from gas, fume, vapour or
         lack of oxygen;
     (d) drowning from an increase in the level of a liquid;
     (e) asphyxiation from a free-flowing solid, or entrapment by one.
   Duty hierarchy (regs 4 & 5): (1) avoid entry; (2) safe system of work if
   entry unavoidable; (3) adequate emergency/rescue arrangements before work
   starts. This tool helps a competent person structure the decision — it does
   not authorise entry. */

interface CheckItem {
  id: string;
  label: string;
}

const SPECIFIED_RISKS: CheckItem[] = [
  { id: "fire", label: "Serious injury from fire or explosion (e.g. flammable atmosphere, residues, hot work)" },
  { id: "gas", label: "Loss of consciousness or asphyxiation from gas, fume, vapour or lack of oxygen" },
  { id: "heat", label: "Loss of consciousness from an increase in body temperature (heat)" },
  { id: "drowning", label: "Drowning from a rising level of liquid" },
  { id: "engulfment", label: "Asphyxiation from, or entrapment by, a free-flowing solid (e.g. grain, sand, flour)" },
];

const ENTRY_CONTROLS: CheckItem[] = [
  { id: "atmos", label: "Atmosphere tested before entry and continuously monitored (calibrated 4-gas: O₂, flammable %LEL, CO, H₂S)" },
  { id: "ventilation", label: "Ventilation provided as needed — natural or forced — to make and keep the atmosphere safe" },
  { id: "isolation", label: "Inflows (pipes, drains, feeds) and energy sources isolated and locked off / proved dead" },
  { id: "access", label: "Access and egress adequate for the entrant and for getting an incapacitated casualty out" },
  { id: "comms", label: "Reliable communications agreed between entrant and attendant (top man)" },
  { id: "lighting", label: "Suitable lighting — intrinsically safe where a flammable atmosphere is possible" },
  { id: "ppe", label: "Correct PPE/RPE selected for the hazards, including escape sets where justified by the risk assessment" },
  { id: "competence", label: "Entrants trained and competent for confined-space work and the specific task" },
  { id: "attendant", label: "Attendant / top man posted outside throughout, with no other duties" },
];

const RESCUE_CONTROLS: CheckItem[] = [
  { id: "nonentry", label: "Non-entry rescue arranged where possible — retrieval line attached to a harness, on a tripod/davit and winch" },
  { id: "team", label: "Trained rescue team and capable, suitable rescue equipment available on site before entry" },
  { id: "resus", label: "Resuscitation equipment provided where the risk assessment shows it is needed" },
  { id: "no999", label: "Plan does NOT rely on calling 999 alone — the fire & rescue service cannot be your sole rescue arrangement" },
  { id: "casualty", label: "Method for handling a suspended or collapsed casualty (suspension trauma) is worked out and practised" },
];

function toggle(set: Set<string>, id: string): Set<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

type Classification =
  | "not-confined"
  | "avoid-entry"
  | "entry-with-controls"
  | "undecided";

export function ConfinedSpaceAssessment() {
  const [location, setLocation] = useState("");
  const [enclosed, setEnclosed] = useState<"yes" | "no" | "">("");
  const [risks, setRisks] = useState<Set<string>>(new Set());
  const [avoidable, setAvoidable] = useState<"yes" | "no" | "">("");
  // Recorded reasoning for why entry cannot be avoided (reg 4(1) is to avoid
  // entry first; "no" needs a justification a competent person can stand behind).
  const [noEntryJustification, setNoEntryJustification] = useState("");
  const [entryControls, setEntryControls] = useState<Set<string>>(new Set());
  // Atmosphere test record before entry — the readings, time and who took them,
  // plus the monitor's identity and that it has been calibrated / bump-tested.
  const [atmO2, setAtmO2] = useState("");
  const [atmLel, setAtmLel] = useState("");
  const [atmToxic, setAtmToxic] = useState("");
  const [atmTime, setAtmTime] = useState("");
  const [atmTester, setAtmTester] = useState("");
  const [monitorId, setMonitorId] = useState("");
  const [monitorCalibrated, setMonitorCalibrated] = useState(false);
  const [rescueControls, setRescueControls] = useState<Set<string>>(new Set());
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();
  const resultTracked = useRef(false);

  const result = useMemo(() => {
    // Step 1: the two-part definition test.
    const definitionAnswered = enclosed !== "" ;
    const isConfined = enclosed === "yes" && risks.size > 0;

    let classification: Classification = "undecided";
    let level: Verdict = "medium";
    let title = "";
    let advice = "";
    let permitRequired = false;
    let entryGaps: CheckItem[] = [];
    let rescueGaps: CheckItem[] = [];

    if (!definitionAnswered) {
      return null;
    }

    if (!isConfined) {
      classification = "not-confined";
      level = "low";
      title = "Not a confined space under the 1997 Regulations";
      advice =
        enclosed === "no"
          ? "On these answers the place is not substantially enclosed, so the Confined Spaces Regulations 1997 do not apply. A normal risk assessment under the Management Regulations still applies — and remember that enclosure and the work being done can change a space's status, so re-check if conditions change."
          : "The place may be substantially enclosed, but no specified risk has been identified as reasonably foreseeable, so the Confined Spaces Regulations 1997 do not apply on these answers. You must still carry out a normal risk assessment, and re-check if the work, contents or conditions change (for example hot work, fresh residues, or a rising liquid level).";
      return { classification, level, title, advice, permitRequired, entryGaps, rescueGaps, isConfined };
    }

    // It IS a confined space. Step 2: avoidance gate.
    if (avoidable === "") {
      classification = "undecided";
      level = "high";
      title = "This is a confined space — now decide whether entry can be avoided";
      advice =
        "On these answers this is a confined space under the 1997 Regulations. The first legal duty (reg 4(1)) is to avoid entry if the work can reasonably be done another way. Answer the avoidance question to continue.";
      return { classification, level, title, advice, permitRequired, entryGaps, rescueGaps, isConfined };
    }

    if (avoidable === "yes") {
      classification = "avoid-entry";
      level = "low";
      title = "Confined space — do not enter: the work can be done without entry";
      advice =
        "You have said the work can reasonably be done without entering (reg 4(1)). That is the required outcome — do it from outside. Do not enter just because controls are available; entry must be the last resort. Record the no-entry method and the controls that keep people out.";
      return { classification, level, title, advice, permitRequired, entryGaps, rescueGaps, isConfined };
    }

    // Entry is unavoidable: assess the safe system of work + rescue.
    entryGaps = ENTRY_CONTROLS.filter((c) => !entryControls.has(c.id));
    rescueGaps = RESCUE_CONTROLS.filter((c) => !rescueControls.has(c.id));
    classification = "entry-with-controls";
    permitRequired = true;

    const totalGaps = entryGaps.length + rescueGaps.length;
    if (rescueGaps.length > 0) {
      // Missing rescue arrangements is the gravest gap — reg 5 is mandatory.
      level = "veryhigh";
      title = "Confined space entry — NOT safe to proceed yet";
      advice =
        "Entry is unavoidable, so a permit-controlled safe system of work and rescue arrangements are required. Emergency and rescue arrangements (reg 5) must be in place before anyone enters — and on these answers they are incomplete. Do not enter until the gaps below are closed by a competent person.";
    } else if (entryGaps.length > 0) {
      level = "high";
      title = "Confined space entry — close the control gaps before entry";
      advice =
        "Entry is unavoidable. Rescue arrangements are covered, but parts of the safe system of work are still outstanding. A confined-space entry permit is required and every control below must be confirmed by a competent person before entry.";
    } else {
      level = "medium";
      title = "Confined space entry — controls and rescue identified; permit required";
      advice =
        "Entry is unavoidable and you have identified every control and rescue measure in this checklist. This is a high-hazard activity: a confined-space entry permit is still required, controls must be verified on site by a competent person, and atmosphere monitoring must continue throughout. This tool does not authorise entry.";
    }

    return { classification, level, title, advice, permitRequired, entryGaps, rescueGaps, isConfined, totalGaps };
  }, [enclosed, risks, avoidable, entryControls, rescueControls]);

  // Fire the funnel event once per visit when a classification first appears.
  useEffect(() => {
    if (result && result.classification !== "undecided" && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("confined-space-assessment", { classification: result.classification });
    }
  }, [result]);

  const isConfined = enclosed === "yes" && risks.size > 0;
  const showAvoidance = isConfined;
  const showEntryControls = isConfined && avoidable === "no";

  // Required items track the live decision path. Until it's a confirmed
  // confined-space entry, only the definition test is required; once entry is
  // unavoidable the safe-system, atmosphere record and rescue plan all apply.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Location / space assessed", done: location.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
    { label: "Substantially enclosed? answered", done: enclosed !== "" },
    { label: "Specified risks considered", done: enclosed !== "yes" || risks.size > 0 },
  ];
  if (isConfined) {
    requiredItems.push({ label: "Can entry be avoided? answered", done: avoidable !== "" });
    if (avoidable === "no") {
      requiredItems.push({ label: "Justification for why entry is unavoidable", done: noEntryJustification.trim().length > 0 });
      requiredItems.push({ label: "Safe-system controls all confirmed", done: ENTRY_CONTROLS.every((c) => entryControls.has(c.id)) });
      requiredItems.push({ label: "Atmosphere test recorded (O₂, LEL, toxic, time, tester)", done: [atmO2, atmLel, atmToxic, atmTime, atmTester].every((v) => v.trim().length > 0) });
      requiredItems.push({ label: "Gas monitor identified and calibration/bump-test confirmed", done: monitorId.trim().length > 0 && monitorCalibrated });
      requiredItems.push({ label: "Emergency & rescue arrangements all confirmed", done: RESCUE_CONTROLS.every((c) => rescueControls.has(c.id)) });
    }
  }
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="space-y-6">
        {/* Step 1 — definition */}
        <div className="card shadow-soft p-6">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 1</div>
          <h2 className="mt-1 text-xl">Is it a confined space?</h2>
          <p className="mt-1 text-sm text-muted">
            The 1997 Regulations apply only when <strong>both</strong> parts of the test are met: the place is
            substantially enclosed <em>and</em> a specified risk is reasonably foreseeable because of that
            enclosure.
          </p>

          <Field label="Location / space being assessed" className="mt-5">
            <TextInput
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. foul-water manhole, chamber 3, Mill Lane"
            />
          </Field>

          <fieldset className="mt-5">
            <legend className="mb-2 text-[0.85rem] font-semibold text-ink">
              (a) Is the place substantially enclosed? (tank, vat, silo, pit, trench, pipe, sewer, flue, well or
              similar)
            </legend>
            <div className="flex gap-2">
              {(["yes", "no"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setEnclosed(v)}
                  className={`rounded-full border px-5 py-2 text-sm font-semibold capitalize transition-colors ${
                    enclosed === v
                      ? "border-emerald bg-emerald-tint text-emerald-deep"
                      : "border-line-2 text-ink hover:border-ink"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="mt-5">
            <legend className="mb-2 text-[0.85rem] font-semibold text-ink">
              (b) Which specified risks are reasonably foreseeable? (tick all that apply — at least one means a
              specified risk exists)
            </legend>
            <div className="space-y-2">
              {SPECIFIED_RISKS.map((r) => (
                <CheckboxRow key={r.id} checked={risks.has(r.id)} onChange={() => setRisks((s) => toggle(s, r.id))}>
                  {r.label}
                </CheckboxRow>
              ))}
            </div>
          </fieldset>
        </div>

        {/* Step 2 — avoidance gate */}
        {showAvoidance && (
          <div className="card shadow-soft p-6">
            <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 2</div>
            <h2 className="mt-1 text-xl">Can the work be done without entering?</h2>
            <p className="mt-1 text-sm text-muted">
              The first legal duty (reg 4(1)) is to <strong>avoid entry</strong>. Take this seriously before
              answering &ldquo;no&rdquo;: could you use remote cameras or sensors, long-reach tools, or drain,
              purge and clean the space from outside? Entry must be the last resort.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setAvoidable("yes")}
                className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${
                  avoidable === "yes"
                    ? "border-emerald bg-emerald-tint text-emerald-deep"
                    : "border-line-2 text-ink hover:border-ink"
                }`}
              >
                Yes — work can be done from outside
              </button>
              <button
                type="button"
                onClick={() => setAvoidable("no")}
                className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${
                  avoidable === "no"
                    ? "border-emerald bg-emerald-tint text-emerald-deep"
                    : "border-line-2 text-ink hover:border-ink"
                }`}
              >
                No — entry is unavoidable
              </button>
            </div>
            {avoidable === "no" && (
              <Field
                label="Why can the work not be done without entry?"
                hint="Reg 4(1) makes avoiding entry the first duty. Record why a no-entry method (remote tools, cameras/sensors, drain-purge-clean from outside) is not reasonably practicable — a reviewer will challenge a blank here."
                className="mt-4"
              >
                <TextArea
                  value={noEntryJustification}
                  onChange={(e) => setNoEntryJustification(e.target.value)}
                  placeholder="e.g. Blockage is solid and beyond reach of jetting/long-reach tools; CCTV survey confirms manual removal is the only option; space drained and ventilated before entry."
                />
              </Field>
            )}
          </div>
        )}

        {/* Step 3 — entry controls */}
        {showEntryControls && (
          <div className="card shadow-soft p-6">
            <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 3</div>
            <h2 className="mt-1 text-xl">Safe system of work for entry</h2>
            <p className="mt-1 text-sm text-muted">
              Entry is unavoidable, so a safe system of work is required (reg 4(2)). Tick each control only when
              a competent person has confirmed it is actually in place.
            </p>
            <div className="mt-4 space-y-2">
              {ENTRY_CONTROLS.map((c) => (
                <CheckboxRow
                  key={c.id}
                  checked={entryControls.has(c.id)}
                  onChange={() => setEntryControls((s) => toggle(s, c.id))}
                >
                  {c.label}
                </CheckboxRow>
              ))}
            </div>

            {/* Atmosphere test record — readings, who and when, and the monitor. */}
            <div className="mt-4 rounded-xl border border-line-2 bg-white p-4">
              <div className="text-[0.85rem] font-semibold text-ink">Atmosphere test before entry</div>
              <p className="mt-1 text-[0.78rem] text-muted">
                Test in the safe order — oxygen first, then flammable, then toxic — with a calibrated monitor, and
                keep monitoring throughout. Record the actual readings, not just &ldquo;OK&rdquo;.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Field label="Oxygen (O₂) %" hint="Normal ~20.9%; safe range typically 19.5–23.5%.">
                  <TextInput value={atmO2} onChange={(e) => setAtmO2(e.target.value)} placeholder="e.g. 20.9" />
                </Field>
                <Field label="Flammable (% LEL)" hint="Must read 0% LEL before entry.">
                  <TextInput value={atmLel} onChange={(e) => setAtmLel(e.target.value)} placeholder="e.g. 0" />
                </Field>
                <Field label="Toxic (CO / H₂S etc.)" hint="Note gas and reading in ppm.">
                  <TextInput value={atmToxic} onChange={(e) => setAtmToxic(e.target.value)} placeholder="e.g. CO 0 ppm, H₂S 0 ppm" />
                </Field>
                <Field label="Time of test">
                  <TextInput value={atmTime} onChange={(e) => setAtmTime(e.target.value)} placeholder="e.g. 08:15" />
                </Field>
                <Field label="Tested by">
                  <TextInput value={atmTester} onChange={(e) => setAtmTester(e.target.value)} placeholder="Name of competent tester" />
                </Field>
                <Field label="Gas monitor ID / serial">
                  <TextInput value={monitorId} onChange={(e) => setMonitorId(e.target.value)} placeholder="e.g. 4-gas #A1234" />
                </Field>
              </div>
              <div className="mt-3">
                <CheckboxRow checked={monitorCalibrated} onChange={setMonitorCalibrated}>
                  Monitor calibration in date and a bump test passed before use
                </CheckboxRow>
              </div>
            </div>
          </div>
        )}

        {/* Step 4 — rescue */}
        {showEntryControls && (
          <div className="card shadow-soft p-6">
            <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 4</div>
            <h2 className="mt-1 text-xl">Emergency &amp; rescue arrangements</h2>
            <p className="mt-1 text-sm text-muted">
              Reg 5 makes adequate emergency and rescue arrangements a legal duty that must be in place{" "}
              <strong>before</strong> anyone enters. Most confined-space deaths are would-be rescuers — never
              improvise a rescue.
            </p>
            <div className="mt-4 space-y-2">
              {RESCUE_CONTROLS.map((c) => (
                <CheckboxRow
                  key={c.id}
                  checked={rescueControls.has(c.id)}
                  onChange={() => setRescueControls((s) => toggle(s, c.id))}
                >
                  {c.label}
                </CheckboxRow>
              ))}
            </div>
          </div>
        )}

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* Result + printable record */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Assessment completion" />
        </div>
        {result ? (
          <>
            <div className="print-area card shadow-soft p-6" id="confined-space-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Confined space assessment
              </div>
              <h2 className="mt-1 text-lg">
                {location ? `${location} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={result.level} title={result.title}>
                  {result.advice}
                </ResultBanner>
              </div>

              {result.permitRequired && (
                <div className="mt-3 rounded-lg border border-risk-veryhigh/40 bg-[#fdecec] px-4 py-3 text-[0.9rem] font-semibold text-[#a31616]">
                  Permit required: confined-space entry must be controlled by an entry permit.
                  <span className="mt-1 block text-[0.82rem] font-normal text-[#a31616]/90">
                    Confined space entry permit tool coming soon.
                  </span>
                </div>
              )}

              {result.isConfined && (
                <div className="mt-4 text-sm">
                  <div className="font-semibold text-ink">Specified risks identified</div>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5 text-body">
                    {SPECIFIED_RISKS.filter((r) => risks.has(r.id)).map((r) => (
                      <li key={r.id}>{r.label}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.classification === "entry-with-controls" && noEntryJustification.trim() && (
                <div className="mt-4 text-sm">
                  <div className="font-semibold text-ink">Why entry is unavoidable</div>
                  <p className="mt-0.5 whitespace-pre-line text-body">{noEntryJustification}</p>
                </div>
              )}

              {result.classification === "entry-with-controls" &&
                (atmO2 || atmLel || atmToxic || atmTime || atmTester || monitorId) && (
                  <div className="mt-4 text-sm">
                    <div className="font-semibold text-ink">Atmosphere test before entry</div>
                    <ul className="mt-1 space-y-0.5 text-body">
                      <li>O₂: {atmO2 || "—"} · Flammable: {atmLel || "—"} %LEL · Toxic: {atmToxic || "—"}</li>
                      <li>Tested {atmTime ? `at ${atmTime}` : "(time —)"} by {atmTester || "—"}</li>
                      <li>
                        Monitor {monitorId || "—"} —{" "}
                        {monitorCalibrated ? "calibration in date and bump test passed" : "calibration / bump test NOT confirmed"}
                      </li>
                    </ul>
                  </div>
                )}

              {result.classification === "entry-with-controls" && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg bg-mist px-3 py-2.5 text-sm">
                    <div className="font-semibold text-ink">Safe-system controls</div>
                    {result.entryGaps.length === 0 ? (
                      <div className="mt-0.5 text-emerald-deep">All identified.</div>
                    ) : (
                      <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[#b34e10]">
                        {result.entryGaps.map((g) => (
                          <li key={g.id}>{g.label}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="rounded-lg bg-mist px-3 py-2.5 text-sm">
                    <div className="font-semibold text-ink">Rescue arrangements</div>
                    {result.rescueGaps.length === 0 ? (
                      <div className="mt-0.5 text-emerald-deep">All identified.</div>
                    ) : (
                      <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[#a31616]">
                        {result.rescueGaps.map((g) => (
                          <li key={g.id}>{g.label}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              <p className="mt-4 text-[0.8rem] leading-relaxed text-muted">
                This is a structured prompt, not an entry authorisation. The decision to enter a confined space
                must be made on site by a competent person, never from a web tool.
              </p>

              <EvidencePrint value={evidence} briefing />

              <PrintFooter toolName="confined space assessment" />
            </div>
            <GuardedPrintButton
              tool="confined-space-assessment"
              label="Print / save assessment as PDF"
              missing={missingRequired}
              warningTitle="This confined space assessment has missing safety-critical fields"
            />
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the two-part definition test in Step 1 to see whether the Confined Spaces Regulations 1997
            apply and what to do next.
          </div>
        )}
      </div>
    </div>
  );
}
